
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthHeader } from './components/AuthHeader';
import { SignInForm } from './components/SignInForm';
import { SignUpForm } from './components/SignUpForm';
import { useAuth } from '@/contexts/AuthContext';
import { useStripeCheckout } from '@/hooks/useStripeCheckout';
import { toast } from 'sonner';

const Auth = () => {
  // Get mode from URL search params - default to signin if not specified
  const searchParams = new URLSearchParams(window.location.search);
  const modeParam = searchParams.get('mode');
  const resetParam = searchParams.get('reset');
  
  const [mode, setMode] = useState<'signin' | 'signup'>(
    modeParam === 'signup' ? 'signup' : 'signin'
  );
  
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { checkPendingCheckout } = useStripeCheckout();
  const [redirecting, setRedirecting] = useState(false);

  // Effect to update mode if URL params change
  useEffect(() => {
    const currentMode = searchParams.get('mode') === 'signup' ? 'signup' : 'signin';
    setMode(currentMode);
    
    // Check for password reset parameter
    if (resetParam === 'true') {
      toast.success('Password reset link sent! Check your email to complete the process.');
    }
    
    // Check for type=recovery in the URL which happens when a user clicks the reset password link
    const type = searchParams.get('type');
    if (type === 'recovery') {
      // The auth state change handler in AuthContext will handle the redirect after password reset
      toast.info('Please enter your new password');
    }
  }, [searchParams, resetParam]);

  // Effect to handle redirects when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user && !redirecting) {
      console.log('User is authenticated, checking for pending checkout');
      setRedirecting(true);
      
      // Check if there's a pending checkout
      const hasPendingCheckout = checkPendingCheckout();
      
      // If no pending checkout, redirect to chat page
      if (!hasPendingCheckout) {
        console.log('No pending checkout, redirecting to chat page');
        toast.success('Successfully signed in!');
        setTimeout(() => navigate('/chat'), 500);
      } else {
        console.log('Found pending checkout, staying on page for redirect to Stripe');
      }
    }
  }, [user, isAuthenticated, navigate, checkPendingCheckout, redirecting]);

  const toggleMode = () => {
    setMode(mode === 'signin' ? 'signup' : 'signin');
    
    // Update URL without page reload
    const newUrl = new URL(window.location.href);
    if (mode === 'signin') {
      newUrl.searchParams.set('mode', 'signup');
    } else {
      newUrl.searchParams.delete('mode');
    }
    window.history.pushState({}, '', newUrl);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-gray-900 to-gray-800">
      <div className="flex flex-1 items-center justify-center px-4 py-12">
        <div className="w-full max-w-md space-y-8">
          <AuthHeader mode={mode} />
          
          <div className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-2xl border border-gray-700/50 shadow-xl">
            {mode === 'signin' ? (
              <SignInForm toggleMode={toggleMode} />
            ) : (
              <SignUpForm toggleMode={toggleMode} />
            )}
            
            {/* Clear toggle button at the bottom - always visible */}
            <div className="mt-6 text-center">
              <button 
                onClick={toggleMode}
                className="text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors"
                type="button"
              >
                {mode === 'signin' 
                  ? "Don't have an account? Sign up" 
                  : "Already have an account? Sign in"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
